/**
 * `<ToolbarMoreMenu>` — the ⋯ trigger and its searchable menu.
 *
 * Lists every toolbar action that isn't in the row right now (overflowed
 * items first, then the preset's menu-only items) as a grouped, keyboard-
 * navigable listbox behind a search box. Apple's ellipsis for the trigger,
 * the wiki slash popover's anatomy for the panel — same 320px width, same
 * chrome, same scorer — so the two pickers read as siblings.
 *
 * Anatomy + contract (UX spec §D / §E):
 *   - portal to `body`, `position: fixed`, positioned from the trigger via
 *     `resolvePopoverPosition` (align `end`: a 320px box hanging start-
 *     aligned off a trailing button would run past the modal / rail edge);
 *   - the search `<input>` is the ONLY focusable — `role="combobox"` with
 *     `aria-activedescendant` over the listbox, so typing never fights a
 *     highlighted row and the editor's SELECTION survives (only DOM focus
 *     moves; ProseMirror keeps `state.selection`);
 *   - empty query → group headers; any query → flat, badge per row;
 *   - Escape is a CAPTURE listener with `stopPropagation` — the card
 *     `<Modal>` registered its document listener first and would close
 *     the whole card otherwise;
 *   - closes on outside pointerdown, window scroll / resize, after a run,
 *     and when the caller's entry set changes underneath it.
 */

import type { Editor } from '@tiptap/react'
import { Check, CornerDownLeft, Search } from 'lucide-react'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { resolvePopoverPosition } from '@/lib/anchor-popover'

import { ToolbarButton } from './ToolbarButton'
import {
  type MoreMenuEntry,
  type MoreMenuGroup,
  formatShortcut,
  groupMoreMenuEntries,
  searchMoreMenuEntries,
} from './more-menu-entries'
import { MORE_ICON } from './toolbar-items'

const PANEL_WIDTH = 320
const PANEL_MAX_HEIGHT = 384
const SEARCH_BLOCK_HEIGHT = 56
const HEADER_HEIGHT = 24
const ROW_HEIGHT = 36
const PANEL_MARGIN = 6

const GROUP_LABEL_KEY: Record<
  MoreMenuGroup,
  'groupText' | 'groupLists' | 'groupInsert' | 'groupMedia'
> = {
  text: 'groupText',
  lists: 'groupLists',
  insert: 'groupInsert',
  media: 'groupMedia',
}

export interface ToolbarMoreMenuProps {
  editor: Editor
  /** Everything that should appear in the menu, in display order. */
  entries: readonly MoreMenuEntry[]
  tone?: 'default' | 'subtle'
  size?: 'md' | 'sm'
  /** Ref for the overflow hook to measure the trigger. */
  triggerRef?: (node: HTMLButtonElement | null) => void
}

const isMacPlatform = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function ToolbarMoreMenu({
  editor,
  entries,
  tone = 'default',
  size = 'md',
  triggerRef,
}: ToolbarMoreMenuProps) {
  const { t, i18n } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const triggerElRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(
    null,
  )
  const baseId = useId()
  const listboxId = `${baseId}-listbox`
  const optionId = (id: string) => `${baseId}-option-${id}`
  const isMac = useMemo(isMacPlatform, [])

  const results = useMemo(
    () => searchMoreMenuEntries(entries, query, i18n.language),
    [entries, query, i18n.language],
  )
  const grouped = useMemo(
    () => (query.trim().length === 0 ? groupMoreMenuEntries(results) : null),
    [results, query],
  )
  // Flat order used by keyboard navigation — matches the visual order in
  // both the grouped and the filtered branch.
  const flat = useMemo(
    () => (grouped ? grouped.flatMap((bucket) => bucket.entries) : results),
    [grouped, results],
  )

  const entriesKey = entries.map((entry) => entry.id).join(',')

  const close = (refocusEditor: boolean) => {
    setOpen(false)
    setQuery('')
    setHighlight(0)
    if (refocusEditor) editor.commands.focus()
  }

  // The hidden set changed under an open menu (a resize) — close rather
  // than show rows for buttons that just came back into the row.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the entry-id signature by design
  useEffect(() => {
    if (open) close(false)
  }, [entriesKey])

  // Position with a KNOWN size (see anchor-popover: a fixed box near the
  // viewport edge compresses rather than spills, so never measure-then-clamp).
  useLayoutEffect(() => {
    if (!open || !triggerElRef.current) return
    const anchor = triggerElRef.current.getBoundingClientRect()
    const headers = grouped ? grouped.length : 0
    const rows = flat.length === 0 ? 2 : flat.length
    const height = Math.min(
      PANEL_MAX_HEIGHT,
      SEARCH_BLOCK_HEIGHT + headers * HEADER_HEIGHT + rows * ROW_HEIGHT + 8,
    )
    setPosition(
      resolvePopoverPosition({
        anchor,
        size: { width: PANEL_WIDTH, height },
        prefer: 'below',
        align: 'end',
        margin: PANEL_MARGIN,
      }),
    )
  }, [open, grouped, flat.length])

  // Focus the search once the panel EXISTS: on the very first open the
  // position is still null when `open` flips, so the portal (and the
  // input) mount one render later — keyed on `open` alone this effect
  // ran against a null ref and the first menu of every mount was deaf to
  // typing and arrows.
  useEffect(() => {
    if (!open || !position) return
    inputRef.current?.focus()
  }, [open, position])

  // Outside pointerdown, scroll, resize → close. Escape → close + refocus
  // the editor (CAPTURE so the card Modal never sees it).
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target) || triggerElRef.current?.contains(target)) return
      close(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      close(true)
    }
    // Capture-phase `scroll` also delivers the panel's OWN list scrolling
    // (a wheel over the rows, `scrollIntoView` while arrowing) — that must
    // not close the menu.
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return
      close(false)
    }
    const onResize = () => close(false)
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  })

  // Keep the highlighted row in view while arrowing.
  useEffect(() => {
    if (!open || !listRef.current) return
    const active = flat[highlight]
    if (!active) return
    const el = listRef.current.querySelector(`#${CSS.escape(optionId(active.id))}`)
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'nearest' })
  })

  const run = (entry: MoreMenuEntry) => {
    entry.run()
    close(true)
  }

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const len = flat.length
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (len > 0) setHighlight((h) => (h + 1) % len)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (len > 0) setHighlight((h) => (h + len - 1) % len)
    } else if (event.key === 'Home') {
      event.preventDefault()
      setHighlight(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setHighlight(Math.max(0, len - 1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const entry = flat[highlight]
      if (entry) run(entry)
    } else if (event.key === 'Tab') {
      // Hand focus back to the ⋯ trigger BEFORE the panel unmounts, then let
      // the native Tab continue from it. Closing alone removes the focused
      // input, the browser then has no starting point, and the next stop is
      // the first tabbable in the whole document — which for a card inside a
      // modal is a sidebar link behind the backdrop.
      const trigger = triggerElRef.current
      if (trigger?.isConnected) {
        trigger.focus()
        close(false)
      } else {
        event.preventDefault()
        close(true)
      }
    }
  }

  const activeEntry = flat[highlight]
  const setTriggerRef = (node: HTMLButtonElement | null) => {
    triggerElRef.current = node
    triggerRef?.(node)
  }

  const renderRow = (entry: MoreMenuEntry, index: number, showBadge: boolean) => {
    const Icon = entry.icon
    const selected = index === highlight
    // The row itself is the option (no nested <button>: a focusable inside
    // `role="option"` is an axe `nested-interactive` violation, and the
    // combobox input is the only focusable here by design).
    return (
      <div
        key={entry.id}
        // biome-ignore lint/a11y/useSemanticElements: custom listbox over a combobox input; native <option> can't carry icon + shortcut + badge — same shape as SlashCommandPopover.
        role="option"
        id={optionId(entry.id)}
        aria-selected={selected}
        tabIndex={-1}
        // mousedown (not click) so the run fires before the input blurs
        // and the editor selection is still what the user formatted.
        onMouseDown={(event) => {
          event.preventDefault()
          run(entry)
        }}
        onMouseEnter={() => setHighlight(index)}
        className={`flex h-9 w-full cursor-pointer items-center gap-3 px-3 text-start text-sm ${
          selected ? 'bg-bg-muted text-fg' : 'text-fg hover:bg-bg-muted'
        }`}
      >
        {/* An APPLIED action reads the way a pressed toolbar button does —
            the icon sits in the same accent fill — plus a semibold label
            and a trailing check. The check alone was too quiet to scan. */}
        {entry.active ? (
          <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent text-accent-fg"
            aria-hidden="true"
          >
            <Icon className="h-4 w-4" />
          </span>
        ) : (
          <span
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center"
            aria-hidden="true"
          >
            <Icon className={`h-4 w-4 ${selected ? 'text-fg' : 'text-fg-muted'}`} />
          </span>
        )}
        <span
          className={`min-w-0 flex-1 truncate ${entry.active ? 'font-semibold' : 'font-medium'}`}
        >
          {entry.label}
          {entry.active ? <span className="sr-only"> {t('editor.toolbar.activeHint')}</span> : null}
        </span>
        {entry.active ? (
          <Check className="h-4 w-4 shrink-0 text-fg" strokeWidth={2.5} aria-hidden="true" />
        ) : null}
        {showBadge ? (
          <span className="shrink-0 rounded bg-bg-muted px-1.5 py-0.5 text-[11px] font-medium text-fg-muted">
            {t(`editor.toolbar.${GROUP_LABEL_KEY[entry.group]}`)}
          </span>
        ) : null}
        {selected ? (
          <span
            aria-label={t('editor.toolbar.kbdEnterLabel')}
            title={t('editor.toolbar.kbdEnterLabel')}
            className="inline-flex shrink-0 items-center rounded border border-border-strong bg-bg px-1.5 py-0.5 font-mono text-[11px] font-medium text-fg-muted"
          >
            <CornerDownLeft className="h-2.5 w-2.5" aria-hidden="true" />
          </span>
        ) : entry.shortcut ? (
          <span className="shrink-0 font-mono text-[11px] text-fg-subtle" aria-hidden="true">
            {formatShortcut(entry.shortcut, isMac)}
          </span>
        ) : null}
      </div>
    )
  }

  let rowIndex = 0

  return (
    <>
      <ToolbarButton
        ref={setTriggerRef}
        label={t('editor.toolbar.more')}
        onClick={() => (open ? close(false) : setOpen(true))}
        active={open}
        tone={tone}
        size={size}
        omitPressed
        ariaHasPopup="dialog"
        ariaExpanded={open}
        ariaControls={open ? listboxId : undefined}
      >
        <MORE_ICON size={16} strokeWidth={1.75} aria-hidden="true" />
      </ToolbarButton>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              // Marks this as part of the editor even though it is
              // portalled out of it. A surface that exits edit mode when
              // focus leaves its wrapper has no other way to tell "the
              // user opened my own menu" from "the user clicked away" —
              // and closing on the former makes the menu unusable.
              data-editor-popover=""
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                width: PANEL_WIDTH,
                maxHeight: position.maxHeight,
              }}
              className="z-50 flex flex-col overflow-hidden rounded-lg border border-border-strong bg-bg-elevated shadow-xl dark:shadow-[0_20px_25px_-5px_rgb(0_0_0/0.5),0_8px_10px_-6px_rgb(0_0_0/0.5)] motion-safe:transition-opacity motion-safe:duration-150"
            >
              <div className="relative shrink-0 border-b border-border p-2">
                <Search
                  className="pointer-events-none absolute start-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-muted"
                  aria-hidden="true"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(event) => {
                    // Reset the highlight on every query change (slash-menu pattern).
                    setQuery(event.target.value)
                    setHighlight(0)
                  }}
                  onKeyDown={onInputKeyDown}
                  placeholder={t('editor.toolbar.searchActions')}
                  aria-label={t('editor.toolbar.searchActions')}
                  role="combobox"
                  aria-expanded="true"
                  aria-autocomplete="list"
                  aria-controls={listboxId}
                  aria-activedescendant={activeEntry ? optionId(activeEntry.id) : undefined}
                  autoComplete="off"
                  spellCheck={false}
                  className="h-9 w-full rounded-md border border-border bg-bg ps-8 pe-3 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
              </div>
              <div
                ref={listRef}
                id={listboxId}
                // biome-ignore lint/a11y/useSemanticElements: see the row comment — ARIA listbox on a div is the house pattern.
                role="listbox"
                tabIndex={-1}
                aria-label={t('editor.toolbar.moreListAriaLabel', { count: flat.length })}
                className="min-h-0 flex-1 overflow-y-auto py-1"
              >
                {flat.length === 0 ? (
                  <div className="px-3 py-6 text-center text-sm text-fg-muted">
                    {t('editor.toolbar.noResults')}
                  </div>
                ) : grouped ? (
                  grouped.map((bucket) => (
                    <div key={bucket.group} role="presentation">
                      <div
                        role="presentation"
                        className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-fg-subtle"
                      >
                        {t(`editor.toolbar.${GROUP_LABEL_KEY[bucket.group]}`)}
                      </div>
                      {bucket.entries.map((entry) => renderRow(entry, rowIndex++, false))}
                    </div>
                  ))
                ) : (
                  results.map((entry, index) => renderRow(entry, index, true))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
